import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { mdiLoading } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { $api } from '../../api/queryClient'
import { Button } from '../ui/button'
import { Textarea } from '../ui/textarea'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../ui/dialog'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../ui/select'

/** The suggestion fields the dismissal needs. */
export type DismissableSuggestion = {
    uid: string
    properties?: Record<string, unknown> | null
}

const SNOOZE_OPTIONS = [30, 90, 365] as const

export function invalidateIssueQueries(queryClient: ReturnType<typeof useQueryClient>) {
    queryClient.invalidateQueries({
        predicate: query => {
            const key = query.queryKey as unknown[]
            return key.length >= 2 && key[0] === 'get' &&
                typeof key[1] === 'string' && (key[1] as string).includes('/issues')
        },
    })
}

/** The dismissed_reason loop's UI (spec 2026-07-17-01): capture why the
 * suggestion is refused and how long it must stay quiet. The PATCH writes
 * exactly what the engine's reuse check honors — status closed +
 * ignored_until + the reason merged into properties (PATCH replaces
 * properties wholesale, hence the client-side merge). */
export function DismissSuggestionDialog({
    orgCode,
    suggestion,
    onOpenChange,
}: {
    orgCode: string
    suggestion: DismissableSuggestion | null
    onOpenChange: (open: boolean) => void
}) {
    const { t } = useTranslation('issues')
    const queryClient = useQueryClient()
    const [reason, setReason] = useState('')
    const [snoozeDays, setSnoozeDays] = useState<number>(90)

    const patchIssue = $api.useMutation('patch', '/api/v1/organizations/{orgCode}/issues/{issueUid}', {
        onSuccess: () => {
            invalidateIssueQueries(queryClient)
            setReason('')
            onOpenChange(false)
        },
    })

    const dismiss = () => {
        if (!suggestion) return
        const until = new Date()
        until.setDate(until.getDate() + snoozeDays)
        patchIssue.mutate({
            params: { path: { orgCode, issueUid: suggestion.uid } },
            body: {
                status: 'closed',
                ignored_at: new Date().toISOString(),
                ignored_until: until.toISOString(),
                properties: { ...(suggestion.properties ?? {}), dismissed_reason: reason.trim() },
            },
        })
    }

    return (
        <Dialog open={!!suggestion} onOpenChange={onOpenChange}>
            <DialogContent data-testid="suggestion-dismiss-dialog">
                <DialogHeader>
                    <DialogTitle>{t('suggestion.dismiss_title')}</DialogTitle>
                    <DialogDescription>{t('suggestion.dismiss_description')}</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <Textarea
                        value={reason}
                        onChange={e => setReason(e.target.value)}
                        placeholder={t('suggestion.dismiss_reason_placeholder')}
                        data-testid="suggestion-dismiss-reason"
                    />
                    <Select value={String(snoozeDays)} onValueChange={v => setSnoozeDays(Number(v))}>
                        <SelectTrigger data-testid="suggestion-snooze-select">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {SNOOZE_OPTIONS.map(d => (
                                <SelectItem key={d} value={String(d)}>
                                    {t('suggestion.snooze_days', { count: d })}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="suggestion-dismiss-cancel">
                        {t('suggestion.dismiss_cancel')}
                    </Button>
                    <Button
                        onClick={dismiss}
                        disabled={patchIssue.isPending || reason.trim() === ''}
                        data-testid="suggestion-dismiss-confirm"
                    >
                        {patchIssue.isPending && <AppIcon path={mdiLoading} size="xs" className="mr-1 motion-safe:animate-spin" />}
                        {t('suggestion.dismiss_confirm')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
